import { useState, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { supabase } from '../services/supabase'
import { ArrowLeft } from 'lucide-react'
import PasswordStrengthBar, { isPasswordValid } from '../components/PasswordStrengthBar'
import { Turnstile } from '@marsidev/react-turnstile'
import type { TurnstileInstance } from '@marsidev/react-turnstile'

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string

type AuthState = 'signin' | 'signup' | 'confirm_email' | 'reset_password' | 'reset_sent'

const ERROR_MAP: Record<string, string> = {
  'Invalid login credentials': 'Email o password non corretti',
  'User already registered': 'Email già registrata',
  'Password should be at least 6 characters': 'La password deve essere di almeno 8 caratteri',
}

function mapError(message: string): string {
  return ERROR_MAP[message] ?? message
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

// Common disposable/temporary email domains — reject at signup
const DISPOSABLE_DOMAINS = new Set([
  '0-mail.com', '0815.ru', '0clickemail.com', '0wnd.net', '0wnd.org',
  '10minutemail.com', '10minutemail.net', '10minutemail.org', '10minutemail.de',
  '10minutemail.co.uk', '10minutemail.info', '10minutemail.cf', '10minutemail.ga',
  '10minutemail.gq', '10minutemail.ml', '10minutemail.tk', '10minemail.com',
  '20minutemail.com', '20minutemail.it',
  'burnermail.io', 'burnthis.com',
  'discard.email', 'discardmail.com', 'discardmail.de',
  'disposableaddress.com', 'disposableemailaddresses.com', 'disposableinbox.com',
  'disposablemail.com', 'disposablemails.com',
  'fakeinbox.com', 'fakeinbox.org', 'fakeinbox.net',
  'fakemailgenerator.com', 'fakemailgenerator.net',
  'filzmail.com',
  'getnada.com',
  'guerrillamail.com', 'guerrillamail.net', 'guerrillamail.org',
  'guerrillamail.biz', 'guerrillamail.de', 'guerrillamail.info',
  'guerrillamailblock.com', 'grr.la', 'spam4.me',
  'inboxbear.com',
  'jetable.com', 'jetable.fr.nf', 'jetable.net', 'jetable.org',
  'junk1.com',
  'kasmail.com',
  'klzlk.com',
  'lol.ovpn.to',
  'mailinator.com', 'mailinator.net', 'mailinator.org',
  'mailinator2.com', 'mailinater.com', 'mailnesia.com',
  'mailnull.com', 'mailscrap.com', 'mailslite.com',
  'maildrop.cc', 'mailme.ir', 'mailme24.com',
  'mailnew.com', 'mailtemp.info', 'mailtome.de',
  'maildea.com', 'mailbucket.org', 'mailboxy.fun',
  'maildax.me', 'mailexp.com',
  'mohmal.com',
  'mt2015.com', 'mt2016.com', 'mt2017.com',
  'nada.email', 'nada.ltd',
  'notsharingmy.info',
  'owlpic.com',
  'proxymail.eu',
  'sharklasers.com', 'guerrillamailblock.com',
  'spam.la', 'spamavert.com', 'spamex.com', 'spamfree24.org',
  'spamgourmet.com', 'spamgourmet.net', 'spamgourmet.org',
  'spamgoes.in', 'spamhereplease.com', 'spamhole.com',
  'spamify.com', 'spamspot.com', 'spamthis.co.uk', 'spamthisplease.com',
  'spamtrail.com', 'spamtrap.ro',
  'tempail.com', 'tempalias.com',
  'tempe-mail.com', 'tempemail.co.za', 'tempemail.com',
  'tempemail.net', 'tempemail.org',
  'tempinbox.com', 'tempinbox.co.uk',
  'tempmail.com', 'tempmail.eu', 'tempmail.net',
  'tempmail.org', 'tempmail.de', 'tempmail.it',
  'tempr.email', 'tempsky.com',
  'throwam.com', 'throwam.org',
  'throwaway.email', 'throwam.com',
  'trashmail.at', 'trashmail.com', 'trashmail.de',
  'trashmail.io', 'trashmail.me', 'trashmail.net',
  'trashmail.org', 'trashmail.xyz',
  'trbvm.com',
  'trickmail.net',
  'uggsrock.com',
  'wegwerfmail.de', 'wegwerfmail.net', 'wegwerfmail.org',
  'wh4f.org',
  'xagloo.com', 'xemaps.com', 'xents.com', 'xmaily.com',
  'yopmail.com', 'yopmail.fr', 'yopmail.net',
  'yopmail.org', 'cool.fr.nf', 'jetable.fr.nf',
  'nospam.ze.tc', 'nomail.xl.cx', 'mega.zik.dj', 'speed.1s.fr',
  'courriel.fr.nf', 'moncourrier.fr.nf', 'monemail.fr.nf',
  'monmail.fr.nf',
  'zoemail.org', 'zoemail.com', 'zoemail.net',
])

function isDisposableEmail(email: string): boolean {
  const domain = email.split('@')[1]?.toLowerCase()
  return !!domain && DISPOSABLE_DOMAINS.has(domain)
}

export default function Auth() {
  const initialState: AuthState =
    new URLSearchParams(window.location.search).get('mode') === 'signup' ? 'signup' : 'signin'
  const [state, setState] = useState<AuthState>(initialState)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [disposableError, setDisposableError] = useState('')
  const [loading, setLoading] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string | undefined>()
  const [tosAgreed, setTosAgreed] = useState(false)
  const turnstileRef = useRef<TurnstileInstance>(null)

  function resetCaptcha() {
    turnstileRef.current?.reset()
    setCaptchaToken(undefined)
  }

  function reset() {
    setError('')
    setPassword('')
    setConfirm('')
  }

  function switchTo(next: AuthState) {
    reset()
    setState(next)
  }

  const confirmMismatch = confirm.length > 0 && confirm !== password

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault()
    if (!isValidEmail(email)) { setError('Inserisci un indirizzo email valido'); return }
    setError(''); setLoading(true)
    const { error } = await supabase.auth.signInWithPassword({ email, password, options: { captchaToken } })
    setLoading(false)
    if (error) {
      if (!ERROR_MAP[error.message]) Sentry.captureException(error, { tags: { auth_action: 'signin' } })
      setError(mapError(error.message))
      resetCaptcha()
    } else setCaptchaToken(undefined)
  }

  function handleEmailBlur() {
    if (isValidEmail(email) && isDisposableEmail(email)) {
      setDisposableError('Le email temporanee non sono accettate. Usa un indirizzo email reale.')
    } else {
      setDisposableError('')
    }
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault()
    if (!isValidEmail(email)) { setError('Inserisci un indirizzo email valido'); return }
    if (isDisposableEmail(email)) { setDisposableError('Le email temporanee non sono accettate. Usa un indirizzo email reale.'); return }
    if (!isPasswordValid(password)) { setError('La password deve avere almeno 12 caratteri, una maiuscola e un numero'); return }
    if (password !== confirm) { setError('Le password non coincidono'); return }
    setError(''); setLoading(true)
    const { data, error } = await supabase.auth.signUp({ email, password, options: { captchaToken } })
    if (!error && data.user?.identities?.length === 0) {
      await supabase.auth.resend({ type: 'signup', email })
    }
    setLoading(false)
    if (error) {
      if (!ERROR_MAP[error.message]) Sentry.captureException(error, { tags: { auth_action: 'signup' } })
      setError(mapError(error.message))
      resetCaptcha()
    } else { setCaptchaToken(undefined); setState('confirm_email') }
  }

  async function handleResetRequest(e: React.FormEvent) {
    e.preventDefault()
    if (!isValidEmail(email)) { setError('Inserisci un indirizzo email valido'); return }
    setError(''); setLoading(true)
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset`,
      captchaToken,
    })
    setLoading(false)
    if (error) {
      if (!ERROR_MAP[error.message]) Sentry.captureException(error, { tags: { auth_action: 'reset_request' } })
      setError(mapError(error.message))
      resetCaptcha()
    } else { setCaptchaToken(undefined); setState('reset_sent') }
  }

  if (state === 'confirm_email') {
    return (
      <Card>
        <h1 className="text-2xl font-bold mb-2">Controlla la tua email</h1>
        <p className="text-gray-600 text-sm">
          Ti abbiamo inviato un link di conferma a{' '}
          <span className="font-medium text-gray-900">{email}</span>.
        </p>
        <p className="text-gray-500 text-xs mt-3">
          Non trovi l'email? Controlla la cartella <span className="font-medium">Spam</span> o{' '}
          <span className="font-medium">Promozioni</span> (Gmail).
        </p>
        <button onClick={() => switchTo('signin')} className="mt-6 text-sm text-sky-600 hover:underline">
          Torna al login
        </button>
      </Card>
    )
  }

  if (state === 'reset_sent') {
    return (
      <Card>
        <h1 className="text-2xl font-bold mb-2">Controlla la tua email</h1>
        <p className="text-gray-600 text-sm">
          Ti abbiamo inviato il link per reimpostare la password a{' '}
          <span className="font-medium text-gray-900">{email}</span>.
        </p>
        <button onClick={() => switchTo('signin')} className="mt-6 text-sm text-sky-600 hover:underline">
          Torna al login
        </button>
      </Card>
    )
  }

  if (state === 'reset_password') {
    return (
      <Card>
        <h1 className="text-2xl font-bold mb-6">Reimposta password</h1>
        <form onSubmit={handleResetRequest} className="flex flex-col gap-4">
          <Field label="Email">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="tu@esempio.it" required className={inputCls} />
          </Field>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <Turnstile ref={turnstileRef} siteKey={TURNSTILE_SITE_KEY} options={{ theme: 'light' }} onSuccess={setCaptchaToken} onExpire={resetCaptcha} />
          <button type="submit" disabled={loading || !captchaToken} className={btnCls}>
            {loading ? 'Invio…' : 'Invia link di reset'}
          </button>
        </form>
        <button onClick={() => switchTo('signin')} className="mt-4 text-sm text-sky-600 hover:underline flex items-center gap-1">
          <ArrowLeft size={14} /> Torna al login
        </button>
      </Card>
    )
  }

  if (state === 'signup') {
    return (
      <Card>
        <h1 className="text-2xl font-bold mb-6">Crea account</h1>
        <form onSubmit={handleSignUp} className="flex flex-col gap-4">
          <Field label="Email">
            <input type="email" value={email} onChange={e => { setEmail(e.target.value); setDisposableError('') }}
              onBlur={handleEmailBlur}
              placeholder="tu@esempio.it" required className={inputCls} />
            {disposableError && <p className="text-red-500 text-xs mt-1">{disposableError}</p>}
          </Field>
          <Field label="Password">
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Minimo 12 caratteri" required className={inputCls} />
            <PasswordStrengthBar password={password} />
          </Field>
          <Field label="Conferma password">
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
              placeholder="Ripeti la password" required className={inputCls} />
            {confirmMismatch && (
              <p className="text-red-500 text-xs mt-1">Le password non coincidono.</p>
            )}
          </Field>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={tosAgreed}
              onChange={e => setTosAgreed(e.target.checked)}
              className="mt-0.5 shrink-0 accent-sky-600"
              required
            />
            <span className="text-xs text-gray-500 leading-relaxed">
              Ho letto e accetto i{' '}
              <a href="/termini" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">
                Termini di Servizio
              </a>
              {' '}e la{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">
                Privacy Policy
              </a>.
            </span>
          </label>
          <Turnstile ref={turnstileRef} siteKey={TURNSTILE_SITE_KEY} options={{ theme: 'light' }} onSuccess={setCaptchaToken} onExpire={resetCaptcha} />
          <button type="submit" disabled={loading || !captchaToken || !tosAgreed} className={btnCls}>
            {loading ? 'Registrazione…' : 'Registrati'}
          </button>
        </form>
        <p className="mt-4 text-sm text-gray-500">
          Hai già un account?{' '}
          <button onClick={() => switchTo('signin')} className="text-sky-600 hover:underline">Accedi</button>
        </p>
      </Card>
    )
  }

  // signin (default)
  return (
    <Card>
      <h1 className="text-2xl font-bold mb-6">Accedi</h1>
      <form onSubmit={handleSignIn} className="flex flex-col gap-4">
        <Field label="Email">
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="tu@esempio.it" required className={inputCls} />
        </Field>
        <Field label="Password">
          <input type="password" value={password} onChange={e => setPassword(e.target.value)}
            placeholder="La tua password" required className={inputCls} />
        </Field>
        {error && <p className="text-red-600 text-sm">{error}</p>}
        <Turnstile ref={turnstileRef} siteKey={TURNSTILE_SITE_KEY} options={{ theme: 'light' }} onSuccess={setCaptchaToken} onExpire={resetCaptcha} />
        <button type="submit" disabled={loading || !captchaToken} className={btnCls}>
          {loading ? 'Accesso…' : 'Accedi'}
        </button>
      </form>
      <div className="mt-4 flex justify-between text-sm">
        <p className="text-gray-500">
          Non hai un account?{' '}
          <button onClick={() => switchTo('signup')} className="text-sky-600 hover:underline">Registrati</button>
        </p>
        <button onClick={() => switchTo('reset_password')} className="text-gray-400 hover:underline">
          Password dimenticata?
        </button>
      </div>
    </Card>
  )
}

// ── Shared primitives ──────────────────────────────────────────────────────────

const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-sky-500'
const btnCls = 'w-full bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white font-medium rounded-lg py-2 text-sm transition-colors'

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 w-full max-w-md">
        <a href="/" className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600 mb-6">
          <ArrowLeft size={14} /> Home
        </a>
        {children}
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      {children}
    </div>
  )
}
